// src/components/dashboard/VADashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, FiBriefcase, FiDollarSign, FiStar, FiTrendingUp,
  FiCheckCircle, FiClock, FiUser, FiMessageSquare, FiCalendar,
  FiBarChart2, FiSettings, FiPlus
} from 'react-icons/fi';
import { Sparkles, TrendingUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';
import OpenchatWidget from '../chat/OpenchatWidget';

const VADashboard = () => {
  const { userProfile } = useAuth();
  const [stats, setStats] = useState({
    activeJobs: 0,
    completedJobs: 0,
    totalEarnings: 0,
    avgRating: 0
  });
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [vaProfile, setVaProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [chatOpen, setChatOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  useEffect(() => {
    if (userProfile?.uid) {
      fetchDashboardData();
    }
  }, [userProfile]);

  const fetchDashboardData = async () => {
    if (!userProfile) return;
    
    try {
      setLoading(true);
      
      // Get or create VA profile
      let profile = await jobService.getVAProfile(userProfile.uid);
      if (!profile) {
        const newProfile = await jobService.createVAProfile(userProfile.uid, {
          name: userProfile.displayName || userProfile.email?.split('@')[0],
          email: userProfile.email,
          skills: [],
          hourlyRate: 0,
          bio: '',
          availability: 'available'
        });
        profile = { id: newProfile.id, ...newProfile };
      }
      setVaProfile(profile);

      // Use userId (auth UID) instead of profile.id for job queries
      const vaUserId = profile.userId || userProfile.uid;

      const [vaStats, availableJobsData, myJobsData] = await Promise.all([
        jobService.getVAStats(vaUserId),  // Changed from profile.id to vaUserId
        jobService.getJobs({ status: 'open', limit: 10 }),
        jobService.getVAMyJobs(vaUserId, 5)  // Changed from profile.id to vaUserId
      ]);
      
      setStats(vaStats);
      setAvailableJobs(availableJobsData);
      setMyJobs(myJobsData);
    } catch (error) {
      console.error('Error fetching VA dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-amber-700 bg-amber-100';
      case 'in_progress':
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'assigned': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-emerald-600 bg-emerald-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'rejected': return 'text-rose-600 bg-rose-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (job) => {
    if (job.type === 'application') {
      return 'Pending Application';
    }
    return job.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const openJobChat = (job) => {
    setSelectedChat({
      recipientId: job.clientId,
      recipientName: job.clientName,
      recipientEmail: job.clientEmail,
      jobContext: {
        id: job.id,
        title: job.title,
        status: job.status
      }
    });
    setChatOpen(true);
  };


  const renderQuickActions = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Link
        to="/jobs/browse"
        className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 shadow-md transition-all group"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <FiSearch className="text-white" size={24} />
          </div>
          <span className="text-xs text-blue-600 font-medium uppercase tracking-wide">Opportunities</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Browse Jobs</h3>
        <p className="text-gray-600 text-sm">Find new opportunities that match your skills</p>
      </Link>

      <Link
        to="/profile/va-setup"
        className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100 shadow-md transition-all group"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <FiUser className="text-white" size={24} />
          </div>
          <span className="text-xs text-green-600 font-medium uppercase tracking-wide">Profile</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Update Profile</h3>
        <p className="text-gray-600 text-sm">Enhance your profile to attract more clients</p>
      </Link>

      <Link
        to="/earnings"
        className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-xl border border-purple-100 shadow-md transition-all group"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <FiBarChart2 className="text-white" size={24} />
          </div>
          <span className="text-xs text-purple-600 font-medium uppercase tracking-wide">Analytics</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">View Earnings</h3>
        <p className="text-gray-600 text-sm">Track your income and performance metrics</p>
      </Link>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-24 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {userProfile?.displayName}
          </h1>
          <p className="text-gray-600 mt-2">Manage your virtual assistant services</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Link
            to="/jobs/browse"
            className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiSearch className="mr-2" size={16} />
            Browse Jobs
          </Link>
          
          {!vaProfile?.profileComplete && (
            <Link
              to="/profile/va-setup"
              className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center"
            >
              <FiSettings className="mr-2" size={16} />
              Complete Profile
            </Link>
          )}
        </div>
      </div>

      {/* Profile Completion Banner */}
      {!vaProfile?.profileComplete && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mr-4">
              <Sparkles className="text-white" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-900">Complete your profile to start earning</h3>
              <p className="text-amber-700 mt-1">Add your skills, set your rates, and showcase your experience to attract clients.</p>
            </div>
            <Link
              to="/profile/va-setup"
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeJobs}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FiBriefcase className="text-blue-600" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-4 text-sm">
            <FiTrendingUp className="text-green-500 mr-1" size={14} />
            <span className="text-green-500 font-medium">+2 this week</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Jobs</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.completedJobs}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FiCheckCircle className="text-green-600" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-4 text-sm">
            <span className="text-gray-500">Success rate: 100%</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(stats.totalEarnings)}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FiDollarSign className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-4 text-sm">
            <TrendingUp className="text-green-500 mr-1" size={14} />
            <span className="text-green-500 font-medium">+15% this month</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rating</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.avgRating.toFixed(1)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <FiStar className="text-yellow-600" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <FiStar
                key={star}
                className={`w-4 h-4 ${
                  star <= Math.floor(stats.avgRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {renderQuickActions()}

      {/* Available Jobs & My Jobs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Available Jobs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Available Jobs</h2>
            <Link
              to="/jobs/browse"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All
            </Link>
          </div>
          
          {availableJobs.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {availableJobs.slice(0, 3).map((job) => (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900 line-clamp-1">{job.title}</h3>
                    <span className="text-sm font-medium text-green-600">{formatCurrency(job.budget)}</span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{job.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500 space-x-3">
                      <span className="flex items-center">
                        <FiClock size={12} className="mr-1" />
                        {job.duration}
                      </span>
                      <span className="flex items-center">
                        <FiBriefcase size={12} className="mr-1" />
                        {job.skillsRequired?.slice(0, 2).join(', ')}
                      </span>
                    </div>
                    
                    <Link
                      to={`/jobs/${job.id}`}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      Apply
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <FiBriefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs available</h3>
              <p className="text-gray-600">Check back later for new opportunities</p>
            </div>
          )}
        </div>

        {/* My Jobs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">My Jobs</h2>
            <Link
              to="/jobs/my-jobs"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All
            </Link>
          </div>
          
          {myJobs.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {myJobs.map((job) => (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">{job.title}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <FiDollarSign size={12} className="mr-1" />
                        {formatCurrency(job.budget)}
                      </span>
                      <span className="flex items-center">
                        <FiCalendar size={12} className="mr-1" />
                        {new Date(job.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        View
                      </Link>
                      
                      <button
                        onClick={() => openJobChat(job)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center"
                      >
                        <FiMessageSquare size={16} className="mr-2" />
                        Message Client
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <FiBriefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No active jobs</h3>
              <p className="text-gray-600 mb-4">Start applying to jobs to see them here</p>
              <Link
                to="/jobs/browse"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FiSearch className="mr-2" size={16} />
                Browse Available Jobs
              </Link>
            </div>
          )}
        </div>
      </div>
      {chatOpen && selectedChat && (
        <OpenchatWidget
          recipientId={selectedChat.recipientId}
          recipientName={selectedChat.recipientName}
          recipientEmail={selectedChat.recipientEmail}
          jobContext={selectedChat.jobContext}
          isOpen={chatOpen}
          onToggle={() => setChatOpen(!chatOpen)}
          position="bottom-right"
        />
      )}
    </div>
  );
};

export default VADashboard;